import { useMutation, useQuery } from '@apollo/client';
import { Button, Divider, Grid, IconButton, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getIssueTypes,
  getUsersWithFilters,
  getWorkOrderBidsByWorkOrderId,
  getWorkOrderById,
  getWorkOrderPriorities,
  getWorkOrderStatuses,
  ProcureUser,
  updateWorkOrder as updateWorkOrderMutation,
  deleteWorkOrder as deleteWorkOrderMutation,
  WorkOrderUpdateInput,
  PermissionsEnum,
  getProcureVendorsWithFilters,
  MIXPANEL_EVENTS,
  Configuration,
  CONFIGURATION_TYPE,
  getAssetsWithFilters,
  Asset,
} from 'vendor-marketplace-shared-library';
import { Delete as DeleteIcon } from '@mui/icons-material';
import WorkOrderAttachmentsModule from './components/WorkOrderAttachmentsModule';
import WorkOrderBidsModule from './components/WorkOrderBidsModule';
import WorkOrderCommentsModule from './components/WorkOrderCommentsModule';
import WorkOrderDetailsModule from './components/WorkOrderDetailsModule';
import { useState } from 'react';
import Modal from '../../components/common/Modal';
import useAlert from '../../hooks/useAlert';
import { DELETE_WORK_ORDER } from '../../error-messages';
import useAuth from '../../hooks/useAuth';
import InLineEditTextField from '../../components/common/InLineEditTextField';
import useMixpanel from '../../hooks/useMixpanel';
import useConfigurations from '../../hooks/useConfigurations';
import { getConfigurationValue } from '../../utilities/helpers';

export default function WorkOrderDetailsPage() {
  const { validateWorkOrderUserPermissions } = useAuth();
  const { configurationsQuery } = useConfigurations();
  const params = useParams();
  const navigate = useNavigate();
  const alert = useAlert();
  const { mixpanelTrack } = useMixpanel();
  const [workOrderWatcherUserIds, setWorkOrderWatcherUserIds] = useState<string[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [workOrderAssetIds, setWorkOrderAssetIds] = useState<string[]>([]);

  // Show Concierge Enabled Work Order Module
  const userConciergeFeatureFrontendEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.CONCIERGE_FEATURE_FRONTEND_IS_ENABLED,
    );

  const isUserConciergeFeatureFrontendEnabled = getConfigurationValue(
    userConciergeFeatureFrontendEnabledConfiguration,
  );

  const {
    data: workOrderData,
    refetch,
    loading,
  } = useQuery(getWorkOrderById, {
    variables: {
      id: params.id,
    },
    onCompleted: data => {
      if (!data.getWorkOrderById) {
        navigate('/404');
      }
      setWorkOrderWatcherUserIds(
        data.getWorkOrderById?.workOrderWatcherUsers.map((watcher: ProcureUser) => watcher.id) ??
          [],
      );
      setWorkOrderAssetIds(data?.getWorkOrderById?.assets.map((asset: Asset) => asset.id) ?? []);
    },
  });

  /* Property users query */
  const { data: propertyUsersData } = useQuery(getUsersWithFilters, {
    variables: { propertyIds: workOrderData?.getWorkOrderById?.propertyId },
    skip: !workOrderData?.getWorkOrderById?.propertyId,
  });

  const { data: workOrderStatuses } = useQuery(getWorkOrderStatuses);

  const { data: issueTypeData } = useQuery(getIssueTypes);

  const { data: workOrderPriorityData } = useQuery(getWorkOrderPriorities);

  const { data: procureVendors } = useQuery(getProcureVendorsWithFilters);

  const { data: bidsData } = useQuery(getWorkOrderBidsByWorkOrderId, {
    variables: { workOrderId: workOrderData?.getWorkOrderById?.id },
    skip: !workOrderData?.getWorkOrderById?.id || !isUserConciergeFeatureFrontendEnabled,
  });

  const { data: assetData } = useQuery(getAssetsWithFilters, {
    variables: {
      propertyIds: [workOrderData?.getWorkOrderById?.property.id],
      issueTypeIds: [workOrderData?.getWorkOrderById?.issueType.id],
    },
    skip:
      !workOrderData?.getWorkOrderById?.property.id ||
      !workOrderData?.getWorkOrderById?.issueType.id,
  });

  const [deleteWorkOrder] = useMutation(deleteWorkOrderMutation, {
    variables: {
      id: params.id,
    },
  });

  const [updateWorkOrder] = useMutation(updateWorkOrderMutation, {
    refetchQueries: [getWorkOrderById],
  });

  const onUpdate = async (
    formData: Partial<WorkOrderUpdateInput>,
    mixpanelEvent: string | null,
  ) => {
    await updateWorkOrder({
      variables: {
        id: params.id,
        input: {
          ...formData,
        },
      },
      onError: error => {
        alert.setAlert({ severity: 'error', message: error?.message ?? DELETE_WORK_ORDER });
      },
      onCompleted: () =>
        mixpanelTrack(mixpanelEvent ?? MIXPANEL_EVENTS.WORK_ORDER_UPDATE_COMPLETED),
    });
  };

  const onDelete = async () => {
    await deleteWorkOrder();
    navigate('/');
  };

  if (loading && !workOrderData) return <div>Loading....</div>;

  return (
    <Grid container direction={'column'} rowGap={2}>
      <Grid item container sx={styles.mainContainer}>
        <Grid item>
          {validateWorkOrderUserPermissions(
            workOrderData?.getWorkOrderById,
            PermissionsEnum.WORK_ORDER_DETAILS_UPDATE,
          ) ? (
            <Grid item container sx={styles.title}>
              <Grid item>
                <InLineEditTextField
                  textVariant={'h4'}
                  content={workOrderData?.getWorkOrderById?.title || ''}
                  onSave={(value: string) =>
                    onUpdate(
                      { title: value },
                      MIXPANEL_EVENTS.WORK_ORDER_DETAILS_EDIT_WORK_ORDER_PRESSED,
                    )
                  }
                />
              </Grid>
              <Grid item>
                <Typography variant={'h4'}>{`#${workOrderData?.getWorkOrderById?.id}`}</Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography variant={'h4'}>
              {`${workOrderData?.getWorkOrderById?.title}, #${workOrderData?.getWorkOrderById?.id}`}
            </Typography>
          )}
        </Grid>
        {/*Delete button*/}
        {validateWorkOrderUserPermissions(
          workOrderData?.getWorkOrderById,
          PermissionsEnum.WORK_ORDERS_DELETE,
        ) && (
          <Grid item>
            <IconButton onClick={() => setOpenDeleteModal(true)}>
              <DeleteIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
      <Divider />
      <Grid item container spacing={2}>
        {/* Details container */}
        <Grid item xs={12} md={7}>
          <Grid container rowGap={2}>
            <WorkOrderDetailsModule
              workOrder={workOrderData?.getWorkOrderById}
              users={propertyUsersData?.getUsersWithFilters ?? []}
              workOrderStatuses={workOrderStatuses?.getWorkOrderStatuses ?? []}
              issueTypeData={issueTypeData?.getIssueTypes ?? []}
              workOrderPriorityData={workOrderPriorityData?.getWorkOrderPriorities ?? []}
              procureVendorsData={procureVendors?.getProcureVendorsWithFilters ?? []}
              updateWorkOrder={onUpdate}
              workOrderWatcherUserIds={workOrderWatcherUserIds}
              setWorkOrderWatcherUserIds={setWorkOrderWatcherUserIds}
              workOrderAssetIds={workOrderAssetIds}
              setWorkOrderAssetIds={setWorkOrderAssetIds}
              assets={assetData?.getAssetsWithFilters ?? []}
            />
            {bidsData?.getWorkOrderBidsByWorkOrderId?.length > 0 &&
              isUserConciergeFeatureFrontendEnabled &&
              workOrderData?.getWorkOrderById?.conciergeEnabled && (
                <WorkOrderBidsModule
                  workOrderBids={bidsData?.getWorkOrderBidsByWorkOrderId ?? []}
                />
              )}
            <WorkOrderAttachmentsModule
              attachments={workOrderData?.getWorkOrderById?.attachments ?? []}
              workOrder={workOrderData?.getWorkOrderById ?? {}}
              refetchWorkOrders={refetch}
            />
          </Grid>
        </Grid>
        {/* Comments container */}
        <Grid item xs={12} md={5}>
          <WorkOrderCommentsModule workOrder={workOrderData?.getWorkOrderById} />
        </Grid>
      </Grid>
      <Modal title={'Delete Work Order'} open={openDeleteModal} setOpen={setOpenDeleteModal}>
        <Grid container spacing={2}>
          <Grid item>
            <Typography>
              Are you sure you want to delete this work order? You cannot undo this action.
            </Typography>
          </Grid>
          <Grid item>
            <Button variant='contained' color={'error'} onClick={onDelete}>
              Delete
            </Button>
          </Grid>
        </Grid>
      </Modal>
    </Grid>
  );
}

const styles = {
  mainContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
  },
  title: {
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 2,
  },
};
